import { Injectable, Signal, WritableSignal, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { IdName, MediaInfoListResponse, MediaInfoResponse } from '@key-telematics/fleet-api-client';
import { AppService } from 'app/app.service';
import { MediaService } from 'app/services/media/media.service';
import { FilterPanelFilter } from 'app/shared/components/filter-panel/filter-panel.service';
import { ModalService } from 'app/shared/components/modal';
import { filter, map } from 'rxjs';
import { SaveVideoModalComponent } from '../components/save-video-modal/save-video-modal.component';
import { ListType } from '../videos.component';

/**
 * Video loaded detail interface.
 */
export interface VideoLoadedDetail  {
    id: string;
    status: string;
}

@Injectable({
    providedIn: 'root'
})
export class VideosService {

    clientId: Signal<string> = toSignal(this.app.client$.pipe(
        filter(client => client?.id != null),
        map(client => client.id)
    ));
 
    private selectedVideo: WritableSignal<MediaInfoResponse> = signal(null);
    readonly selectedVideoResults: Signal<MediaInfoResponse> = this.selectedVideo.asReadonly();

    private videoList: WritableSignal<MediaInfoResponse[]> = signal([]);
    readonly videoListResults: Signal<MediaInfoResponse[]> = this.videoList.asReadonly();

    private videoLoaded: WritableSignal<VideoLoadedDetail> = signal({id: "", status: ""});
    readonly videoLoadedResult: Signal<VideoLoadedDetail> = this.videoLoaded.asReadonly();

    setVideoLoaded(videoLoadedDetail: VideoLoadedDetail) {
        this.videoLoaded.set(videoLoadedDetail);
    }


    constructor(
        private app: AppService,
        private media: MediaService,
        private modal: ModalService
    ) {}

    async loadVideos(filters: FilterPanelFilter, forcedOffset?: number, listType?: ListType): Promise<MediaInfoListResponse> {
        const f = [];

        const addFilter = (field: string, items: IdName[], recurse: boolean = false) => {
            if (items && items.length > 0) {
                f.push(`(${items.map(item => `eq(${field},${item.id}${recurse ? '/*' : ''})`).join('|')})`);
            }
        };

        // If the event filter 'AI Event' has been selected sending eq(eventClass, aievent) to the BE will not return ai videos. The BE has not been setup for this.
        // General note/reminder: The BE doesnt know the terms 'AI Triggered' or 'AI Events', this is handled between api.masker.ts + fleet-shared-ui
        const aiEventFilterIndex = filters.filters?.eventClass?.findIndex(x => x.id === 'aievent');
        const triggeredEventFilterIsSelected = filters.filters?.eventClass?.find(x => x.id === 'triggeredevent');

        const addEventFilter = (items: IdName[], recurse: boolean = false) => {
            const aiEventItems = [
                { id: 'driver_smoking'},
                { id: 'driver_on_phone'},
                { id: 'driver_undetected'},
                { id: 'camera_obstructed'}
            ];
            
            const allEventFilters = [];

            if (items && items.length > 0) {
                items.forEach(item => {
                    allEventFilters.push(`eq(eventClass,${item.id}${recurse ? '/*' : ''})`);
                })
            }

            if (aiEventFilterIndex >= 0) {
                aiEventItems.forEach(item => {
                    allEventFilters.push(`eq(eventType,${item.id}${recurse ? '/*' : ''})`);
                })
            }

            const filterString = `(${allEventFilters.join('|')})`;
            if (allEventFilters?.length > 0) {
                f.push(filterString);
            }

            // 'triggered event' selected and not 'ai event'
            // (the issue is 'triggered events' return ai events too as they are 'triggered events' to the BE)
            if (items?.length > 0 && aiEventFilterIndex < 0 && !!triggeredEventFilterIsSelected) {
                const rawString = '(out(labels,ai))';
                const encoded = encodeURI(rawString);
                f.push(encoded);
            }
        };

        addFilter('assetId', filters.filters?.assets);
        addFilter('status', filters.filters?.status);
        addFilter('requesterId', filters.filters?.requester);
        addEventFilter(filters.filters?.eventClass);
        addFilter('input', filters.filters?.input);
        addFilter('accessGroupId', filters.filters?.costcentre?.filter(x => x.id), true);
        addFilter('assetGroupId', filters.filters?.assetgroup?.filter(x => x.id), true);
        addFilter('assetCategoryId', filters.filters?.assetcategory?.filter(x => x.id), true);

        if (filters.date?.start) {
            f.push(`ge(date,epoch:${new Date(filters.date.start).getTime()}),le(date,epoch:${new Date(filters.date.end).getTime()})`);
        }

        if (listType === 'ai triggered') {
            const rawString = '(in(labels,ai)&out(labels,src/user))';
            const encoded = encodeURI(rawString);
            f.push(encoded);
        }

        f.push('sort(-date)');
        let offset;
        if (forcedOffset) {
            offset = forcedOffset;
        } else if (filters.page)  {
            offset = (filters.page - 1) * filters.limit;
        } else {
            offset = 0;
        }

        const itemsRawResponse = await this.app.api.media.listMedia(this.clientId(), offset, filters.limit || 20, f.join(','));

        // this is returns a promise for the: overview component --> video details
        return itemsRawResponse;
    }

    async getVideo(id: string): Promise<MediaInfoResponse> {
        return new Promise<MediaInfoResponse>(resolve => {
            setTimeout(() => { // fake an async request, as callers are expecting this to be asynchronous
                if (this.videoListResults()) {
                    resolve(this.videoListResults().find(x => x.id === id));
                }
                return resolve(null);
            }, 0);
        }).then(item => {
            if (!item) {
                return this.app.api.media.getMedia(id);
            }
            return item;
        });
    }

    updateVideoList(list: MediaInfoResponse[]) {
        this.videoList.set(list);
    }

    setSelectedVideo(video: MediaInfoResponse) {
        this.selectedVideo.set(video);
    }

    async saveVideo(video: MediaInfoResponse, isMobile: boolean) {
            // on a mobile the 'save video confirmation screen opens' in the 'videos.html' view
            // i.e. not in a modal. This lets the UI know to do this + which header to display. 
            this.media.setSaveVideoViewState(true);

            // the 'save video confirmation' component is opened in the 'videos.html' view
            // or inside a modal (on desktops). This allows it too always know which video to save 
            this.media.setVideoToSaveDetails(video);

            if (!isMobile) {
                const result = await this.confirmSavingVideo();
                if (result) {
                    try {
                        await this.media.saveVideo(video.id);
                        this.media.updateDisplaySaveMessageFlag(true);
                        this.media.setSaveVideoViewState(false);
                        this.modal.close();
                    } catch (err) {
                        console.error(err);
                        this.media.setSaveVideoViewState(false);
                        this.modal.error(err.message);
                        this.modal.close();
                    }
                } else {
                    this.media.setSaveVideoViewState(false);
                    this.modal.close();
                }
            }
    }

    async confirmSavingVideo(): Promise<boolean> {
        return new Promise<boolean>((resolve, _reject) => { this.modal.open(SaveVideoModalComponent, {
                actions: {
                    close: () => {
                        resolve(false);
                    },
                    save: () => {
                        resolve(true);
                    }    
                },
            });
        })
    }
}
